import type { ReviewPackTemplate } from '../review-pack';

export const QUARTERLY_BUSINESS_REVIEW_TEMPLATE = {
  templateId: 'logistics-qbr-default',
  cadence: 'quarterly_business_review',
  industryProfileId: 'logistics-default',
  audience: 'board',
  sections: [
    {
      sectionId: 'board_headline',
      title: 'Board headline and decision ask',
      purpose: 'Open the QBR with decision memory, strategic drift, and board ask.',
      required: true,
      sourceAnswerFamily: 'decision_ledger_review_queue',
      plannerArchetype: 'review_pack',
      sourceCompositionPath: 'packages/lib/src/decision-ledger.ts',
      requiredInputs: ['decision_ledger_board_rollup'],
      freshnessRequirement: { maxAgeHours: 168, staleBehavior: 'block' },
      proofRequirement: 'proof_ref_required',
      actionAffordances: ['annotate', 'adopt_into_monitoring', 'share_snapshot'],
    },
    {
      sectionId: 'qbr_what_changed',
      title: 'What changed since last review',
      purpose: 'Name the facts and commitments that changed the board decision frame.',
      required: true,
      sourceAnswerFamily: 'exec_brief_v2',
      plannerArchetype: 'review_pack',
      sourceCompositionPath: 'apps/api/src/ask-helix/ask-helix.exec-brief-v2.ts',
      requiredInputs: ['brief_live_providers', 'decision_ledger_timeline'],
      freshnessRequirement: { maxAgeHours: 168, staleBehavior: 'block' },
      proofRequirement: 'proof_ref_required',
      actionAffordances: ['annotate', 'share_snapshot'],
    },
    {
      sectionId: 'quarter_variance_bridge',
      title: 'Performance and variance deep-dive',
      purpose: 'Preserve the governed variance bridge for the closed quarter.',
      required: true,
      sourceAnswerFamily: 'variance_decomposition',
      plannerArchetype: 'review_pack',
      sourceCompositionPath:
        'apps/api/src/ask-helix/variance/variance-decomposition-composition.ts',
      requiredInputs: ['promoted_quarter_metric_facts', 'latest_monthly_bridge'],
      freshnessRequirement: { maxAgeHours: 168, staleBehavior: 'allow' },
      proofRequirement: 'proof_ref_required',
      actionAffordances: ['annotate', 'share_snapshot'],
    },
    {
      sectionId: 'qbr_segment_posture',
      title: 'Segment posture',
      purpose: 'Show segment or business-unit posture reconciled to enterprise truth.',
      required: true,
      sourceAnswerFamily: 'dimensional_readout',
      plannerArchetype: 'review_pack',
      sourceCompositionPath:
        'apps/api/src/ask-helix/dimensional/dimensional-readout-composition.ts',
      requiredInputs: ['segment_metric_facts', 'enterprise_reconciliation'],
      freshnessRequirement: { maxAgeHours: 168, staleBehavior: 'block' },
      proofRequirement: 'proof_ref_required',
      actionAffordances: ['annotate', 'share_snapshot'],
    },
    {
      sectionId: 'qbr_path_dependency_commitments',
      title: 'Path dependency and commitments',
      purpose: 'Show which bets, teams, and commitments move with this decision.',
      required: true,
      sourceAnswerFamily: 'drift_realization',
      plannerArchetype: 'review_pack',
      sourceCompositionPath:
        'apps/api/src/ask-helix/drift/drift-realization-intent.ts',
      requiredInputs: ['realization_commitments', 'active_package_links'],
      freshnessRequirement: { maxAgeHours: 168, staleBehavior: 'block' },
      proofRequirement: 'proof_ref_required',
      actionAffordances: ['annotate', 'adopt_into_monitoring', 'share_snapshot'],
    },
    {
      sectionId: 'qbr_forward_trajectory',
      title: 'Forward trajectory and plan pressure',
      purpose: 'Compare latest approved plan trajectory against observed history.',
      required: true,
      sourceAnswerFamily: 'trajectory_forecast',
      plannerArchetype: 'review_pack',
      sourceCompositionPath:
        'apps/api/src/ask-helix/trajectory/trajectory-forecast-composition.ts',
      requiredInputs: ['approved_plan_baseline', 'actual_history'],
      freshnessRequirement: { maxAgeHours: 168, staleBehavior: 'block' },
      proofRequirement: 'proof_ref_required',
      actionAffordances: ['annotate', 'share_snapshot'],
    },
    {
      sectionId: 'qbr_decisions_control_plan',
      title: 'Decisions, tradeoffs, and control plan',
      purpose: 'Review the decision docket and control plan before activation.',
      required: true,
      sourceAnswerFamily: 'decision_ledger_review_queue',
      plannerArchetype: 'review_pack',
      sourceCompositionPath: 'packages/lib/src/decision-ledger.ts',
      requiredInputs: ['decision_ledger_review_queue', 'workspace_action_gates'],
      freshnessRequirement: { maxAgeHours: 168, staleBehavior: 'block' },
      proofRequirement: 'proof_ref_required',
      actionAffordances: ['annotate', 'adopt_into_monitoring', 'share_snapshot'],
    },
    {
      sectionId: 'qbr_evidence_gaps',
      title: 'Evidence, assumptions, and data gaps',
      purpose: 'List proof refs, assumptions, stale sections, and owners for recovery.',
      required: true,
      sourceAnswerFamily: 'exec_brief_v2',
      plannerArchetype: 'review_pack',
      sourceCompositionPath: 'apps/api/src/ask-helix/ask-helix.exec-brief-v2.ts',
      requiredInputs: ['proof_refs', 'binding_gaps', 'stale_sections'],
      freshnessRequirement: { maxAgeHours: 168, staleBehavior: 'block' },
      proofRequirement: 'proof_ref_required',
      actionAffordances: ['annotate', 'share_snapshot'],
    },
  ],
  freshnessPolicy: { generatedAtRequired: true, sectionAsOfRequired: true },
  requiredPermissions: ['ask_helix:read', 'decision_agent:read'],
  sharePolicy: 'immutable_snapshot',
  decisionLedgerCadence: 'quarterly',
} as const satisfies ReviewPackTemplate;
